import { useState, type ReactNode } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Gavel,
  CheckCircle,
  Circle,
  WarningCircle,
  CaretDown,
  CaretUp,
  VideoCamera,
  MapPin,
  Scales,
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import {
  fetchMatterCourtSituation,
  updateCourtPrep,
  setCourtConfidence,
  type CourtSituation,
  type CourtConfidence,
  type CourtPrepItem,
} from '@/lib/mattersApi';
import { getApiErrorMessage } from '@/lib/api';

type Urgency = 'today' | 'tomorrow' | 'this_week';
const URGENCY: Record<Urgency, { label: (d: number) => string; ring: string; chip: string; dot: string }> = {
  today: { label: () => 'In court today', ring: 'border-red-300 dark:border-red-500/40', chip: 'text-red-700 dark:text-red-300', dot: 'bg-red-500' },
  tomorrow: { label: () => 'Court tomorrow', ring: 'border-amber-300 dark:border-amber-500/40', chip: 'text-amber-800 dark:text-amber-300', dot: 'bg-amber-500' },
  this_week: { label: (d) => `Court in ${d} days`, ring: 'border-border', chip: 'text-muted-foreground', dot: 'bg-slate-400' },
};

const CONFIDENCE_META: Record<CourtConfidence, { label: string; cls: string }> = {
  yes: { label: 'Ready', cls: 'text-emerald-700 dark:text-emerald-300' },
  almost: { label: 'Almost ready', cls: 'text-amber-700 dark:text-amber-300' },
  no: { label: 'Not ready', cls: 'text-red-700 dark:text-red-300' },
  not_set: { label: 'Not confirmed', cls: 'text-muted-foreground' },
};

// Static (non-dynamic) classes so Tailwind doesn't purge them.
const CONF_ACTIVE: Record<CourtConfidence, string> = {
  yes: 'bg-emerald-600 text-white',
  almost: 'bg-amber-500 text-white',
  no: 'bg-red-600 text-white',
  not_set: '',
};

export function MatterCourtSituation({ matterId }: { matterId: string }) {
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);

  const q = useQuery<CourtSituation>({
    queryKey: ['matter-court-situation', matterId],
    queryFn: () => fetchMatterCourtSituation(matterId),
    staleTime: 30_000,
  });

  const setData = (next: CourtSituation) => qc.setQueryData(['matter-court-situation', matterId], next);

  const prepMut = useMutation({
    mutationFn: (v: { hearingId: string; panel: 'prof' | 'ops'; key: string; done: boolean }) =>
      updateCourtPrep(v.hearingId, v.panel, v.key, v.done),
    onSuccess: setData,
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not update preparation')),
  });

  const confMut = useMutation({
    mutationFn: (v: { hearingId: string; value: CourtConfidence }) => setCourtConfidence(v.hearingId, v.value),
    onSuccess: (next) => { setData(next); if (next.confidence === 'yes') toast.success('Marked ready for court'); },
    onError: (e) => toast.error(getApiErrorMessage(e, 'Could not record your readiness')),
  });

  const s = q.data;
  if (!s || !s.active || !s.hearing) return null;

  const u = URGENCY[(s.urgency ?? 'this_week') as Urgency];
  const h = s.hearing;
  const conf = CONFIDENCE_META[s.confidence ?? 'not_set'];
  const time = new Date(h.date).toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
  const dateLabel = new Date(h.date).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short' });

  const toggle = (panel: 'prof' | 'ops', item: CourtPrepItem) =>
    prepMut.mutate({ hearingId: h.id, panel, key: item.key, done: !item.done });

  return (
    <div className={`rounded-md border ${u.ring} bg-card`}>
      {/* Headline */}
      <div className="flex items-start justify-between gap-4 px-4 py-3">
        <div className="min-w-0">
          <div className="flex items-center gap-1.5">
            <Gavel size={15} weight="duotone" className={u.chip} />
            <span className={`text-xs font-semibold tracking-tight ${u.chip}`}>{u.label(s.daysUntil ?? 0)}</span>
            <span className="text-border">·</span>
            <span className={`text-xs font-medium ${conf.cls}`}>{conf.label}</span>
          </div>
          <p className="mt-1 truncate text-sm font-medium text-foreground">{h.typeLabel} · {time}</p>
          <div className="mt-0.5 flex flex-wrap items-center gap-x-2 text-[11px] text-muted-foreground">
            <span>{dateLabel}</span>
            {h.court && <><span className="text-border">·</span><span className="flex items-center gap-1"><MapPin size={11} />{h.court}{h.courtroom ? `, ${h.courtroom}` : ''}</span></>}
            {h.judge && <><span className="text-border">·</span><span>{h.judge}</span></>}
            {h.isVirtual && <><span className="text-border">·</span><span className="flex items-center gap-1 text-brand-600 dark:text-brand-400"><VideoCamera size={11} weight="fill" />Remote</span></>}
          </div>
          {s.recommendedAction && !open && (
            <p className="mt-1.5 text-[11px] font-medium text-brand-600 dark:text-brand-400">Next: {s.recommendedAction.label}</p>
          )}
        </div>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="flex shrink-0 items-center gap-1.5 rounded-md bg-brand-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-brand-700"
        >
          {open ? <CaretUp size={13} weight="bold" /> : <CaretDown size={13} weight="bold" />}
          {open ? 'Close' : 'Prepare'}
        </button>
      </div>

      {/* Preparation mode */}
      {open && (
        <div className="space-y-4 border-t border-border px-4 py-4">
          {/* Layer 1 — objective (auto-derived, read-only) */}
          {s.objective && s.objective.length > 0 && (
            <div>
              <h4 className="mb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-muted-foreground">On file</h4>
              <div className="flex flex-wrap gap-x-4 gap-y-1">
                {s.objective.map((o) => (
                  <span key={o.key} className="flex items-center gap-1.5 text-xs">
                    {o.present
                      ? <CheckCircle size={13} weight="fill" className="text-emerald-600 dark:text-emerald-400" />
                      : <WarningCircle size={13} weight="fill" className="text-amber-500" />}
                    <span className={o.present ? 'text-foreground' : 'text-muted-foreground'}>
                      {o.label}{o.detail ? ` (${o.detail})` : ''}
                    </span>
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Layer 2 — professional & operational panels */}
          <div className="grid gap-4 sm:grid-cols-2">
            <PrepPanel
              icon={<Scales size={13} weight="duotone" className="text-brand-600 dark:text-brand-400" />}
              title="Professional"
              subtitle={`${s.professional?.done ?? 0}/${s.professional?.total ?? 0}`}
              items={s.professional?.items ?? []}
              onToggle={(it) => toggle('prof', it)}
              pending={prepMut.isPending}
            />
            <PrepPanel
              icon={<MapPin size={13} weight="duotone" className="text-slate-500" />}
              title="Operational"
              subtitle={`${s.operational?.done ?? 0}/${s.operational?.total ?? 0}`}
              items={s.operational?.items ?? []}
              onToggle={(it) => toggle('ops', it)}
              pending={prepMut.isPending}
            />
          </div>

          {/* Layer 3 — confidence declaration (human only) */}
          <div className="rounded-md border border-border bg-muted/30 px-3 py-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-xs font-medium text-foreground">Am I ready for court?</p>
                <p className="text-[11px] text-muted-foreground">Only you can judge this — the checklist can't.</p>
              </div>
              <div className="flex items-center gap-1.5">
                {(['no', 'almost', 'yes'] as CourtConfidence[]).map((v) => {
                  const active = s.confidence === v;
                  return (
                    <button
                      key={v}
                      type="button"
                      disabled={confMut.isPending}
                      onClick={() => confMut.mutate({ hearingId: h.id, value: v })}
                      className={`rounded-md px-2.5 py-1 text-xs font-medium capitalize ${
                        active
                          ? CONF_ACTIVE[v]
                          : 'border border-border bg-background text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      {v === 'almost' ? 'Almost' : v}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function PrepPanel({
  icon, title, subtitle, items, onToggle, pending,
}: {
  icon: ReactNode;
  title: string;
  subtitle: string;
  items: CourtPrepItem[];
  onToggle: (item: CourtPrepItem) => void;
  pending: boolean;
}) {
  return (
    <div className="rounded-md border border-border">
      <div className="flex items-center justify-between border-b border-border px-3 py-1.5">
        <span className="flex items-center gap-1.5 text-xs font-medium text-foreground">{icon}{title}</span>
        <span className="text-[11px] tabular-nums text-muted-foreground">{subtitle}</span>
      </div>
      <ul className="divide-y divide-border">
        {items.map((it) => (
          <li key={it.key}>
            <button
              type="button"
              disabled={pending}
              onClick={() => onToggle(it)}
              className="flex w-full items-center gap-2 px-3 py-1.5 text-left hover:bg-muted/40 disabled:opacity-60"
            >
              {it.done
                ? <CheckCircle size={14} weight="fill" className="shrink-0 text-emerald-600 dark:text-emerald-400" />
                : <Circle size={14} className="shrink-0 text-muted-foreground" />}
              <span className={`text-xs ${it.done ? 'text-muted-foreground line-through' : 'text-foreground'}`}>{it.label}</span>
            </button>
          </li>
        ))}
      </ul>
    </div>
  );
}
